import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  CreditCard, 
  Link2, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Building2,
  FileText,
  DollarSign,
  ExternalLink,
  Clock,
  ArrowRight,
  Loader2,
  Banknote,
  Users,
  ChevronDown,
  ChevronRight,
  Search
} from 'lucide-react';
import AdminSidebar from '../components/layout/AdminSidebar';
import { MercuryPaymentsTab } from '../components/billpay/MercuryPaymentsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const apiFetch = (url, opts = {}) => fetch(url, { credentials: 'include', cache: 'no-store', ...opts });

export default function BillPayPage() {
  const [status, setStatus] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [bills, setBills] = useState([]);
  const [paymentCounts, setPaymentCounts] = useState(null);
  const [mercuryRecipients, setMercuryRecipients] = useState([]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingPayments, setSyncingPayments] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientDropdownOpen, setRecipientDropdownOpen] = useState(null);
  const [vendorSearch, setVendorSearch] = useState('');
  const [billFilter, setBillFilter] = useState('needs_action'); // 'all', 'needs_action', 'pending_recipient', 'queued', 'sent_to_mercury', 'paid', 'voided'
  const [billSearch, setBillSearch] = useState('');
  const dropdownRef = useRef(null);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setRecipientDropdownOpen(null);
        setRecipientSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get sorted and filtered recipients
  const getFilteredRecipients = () => {
    return mercuryRecipients
      .filter(r => {
        const name = (r.name || r.id || '').toLowerCase();
        return name.includes(recipientSearch.toLowerCase());
      })
      .sort((a, b) => {
        const nameA = (a.name || a.id || '').toLowerCase();
        const nameB = (b.name || b.id || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  };

  // Get filtered and sorted vendors
  const getFilteredVendors = () => {
    return vendors
      .filter(v => {
        const name = (v.qbo_vendor_name || '').toLowerCase();
        const id = (v.qbo_vendor_id || '').toLowerCase();
        const search = vendorSearch.toLowerCase();
        return name.includes(search) || id.includes(search);
      })
      .sort((a, b) => {
        const nameA = (a.qbo_vendor_name || '').toLowerCase();
        const nameB = (b.qbo_vendor_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  };

  // Get filtered bills based on selected filter and search
  const getFilteredBills = () => {
    let filtered = bills;
    
    // Apply status filter
    if (billFilter === 'needs_action') {
      // Show unpaid bills that need attention (pending_recipient or queued but not sent)
      filtered = filtered.filter(b => 
        b.status === 'pending_recipient' || b.status === 'possible_duplicate' ||
        (b.status === 'queued' && !b.sent_to_mercury_at)
      );
    } else if (billFilter === 'unpaid') {
      // All bills that are not paid or voided
      filtered = filtered.filter(b => 
        b.status !== 'paid' && b.status !== 'voided' && b.status !== 'deleted_in_qbo'
      );
    } else if (billFilter === 'voided') {
      filtered = filtered.filter(b => b.status === 'voided' || b.status === 'deleted_in_qbo');
    } else if (billFilter !== 'all') {
      filtered = filtered.filter(b => b.status === billFilter);
    }
    
    // Apply search filter
    if (billSearch.trim()) {
      const search = billSearch.toLowerCase();
      filtered = filtered.filter(b => 
        (b.vendor_name || '').toLowerCase().includes(search) ||
        (b.memo || '').toLowerCase().includes(search) ||
        (b.invoice_number || '').toLowerCase().includes(search)
      );
    }
    
    // Sort: pending_recipient first, then by due date
    return filtered.sort((a, b) => {
      // Pending recipient always first
      if (a.status === 'pending_recipient' && b.status !== 'pending_recipient') return -1;
      if (b.status === 'pending_recipient' && a.status !== 'pending_recipient') return 1;
      
      // Then by due date (earliest first, null dates last)
      if (a.due_date && b.due_date) {
        return new Date(a.due_date) - new Date(b.due_date);
      }
      if (a.due_date && !b.due_date) return -1;
      if (!a.due_date && b.due_date) return 1;
      
      return 0;
    });
  };

  // Get bill counts for filter badges
  const getBillCounts = () => {
    const counts = {
      all: bills.length,
      needs_action: bills.filter(b => 
        b.status === 'pending_recipient' || b.status === 'possible_duplicate' ||
        (b.status === 'queued' && !b.sent_to_mercury_at)
      ).length,
      unpaid: bills.filter(b => b.status !== 'paid' && b.status !== 'voided' && b.status !== 'deleted_in_qbo').length,
      pending_recipient: bills.filter(b => b.status === 'pending_recipient').length,
      queued: bills.filter(b => b.status === 'queued').length,
      sent_to_mercury: bills.filter(b => b.status === 'sent_to_mercury').length,
      possible_duplicate: bills.filter(b => b.status === 'possible_duplicate').length,
      paid: bills.filter(b => b.status === 'paid').length,
      voided: bills.filter(b => b.status === 'voided' || b.status === 'deleted_in_qbo').length,
    };
    return counts;
  };

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/status`);
      if (resp.ok) {
        const data = await resp.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch status:', err);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/vendors`);
      if (resp.ok) {
        const data = await resp.json();
        setVendors(data.vendors || []);
      }
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  }, []);

  const fetchBills = useCallback(async () => {
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/bills`);
      if (resp.ok) {
        const data = await resp.json();
        setBills(data.bills || []);
      }
    } catch (err) {
      console.error('Failed to fetch bills:', err);
    }
  }, []);

  const fetchMercuryRecipients = useCallback(async () => {
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/mercury/recipients`);
      if (resp.ok) {
        const data = await resp.json();
        setMercuryRecipients(data.recipients || []);
      }
    } catch (err) {
      console.error('Failed to fetch Mercury recipients:', err);
    }
  }, []);

  const fetchPaymentStats = useCallback(async () => {
    try {
      const [resp, countsResp] = await Promise.all([
        apiFetch(`${API_URL}/api/billpay/payments/stats`),
        apiFetch(`${API_URL}/api/billpay/payments/counts`),
      ]);
      if (resp.ok) setPaymentStats(await resp.json());
      if (countsResp.ok) setPaymentCounts(await countsResp.json());
    } catch (err) {
      console.error('Failed to fetch payment stats:', err);
    }
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchStatus(),
      fetchVendors(),
      fetchBills(),
      fetchMercuryRecipients(),
      fetchPaymentStats(),
    ]);
    setLoading(false);
  }, [fetchStatus, fetchVendors, fetchBills, fetchMercuryRecipients, fetchPaymentStats]);

  useEffect(() => {
    loadAllData();
    
    // Check for OAuth success in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth') === 'success') {
      showMessage('success', 'QuickBooks Online connected successfully!');
      window.history.replaceState({}, '', '/admin/billpay');
    }
  }, [loadAllData]);

  const syncVendors = async () => {
    setSyncing(true);
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/sync-vendors`, { method: 'POST' });
      if (resp.ok) {
        const data = await resp.json();
        showMessage('success', data.message);
        await fetchVendors();
      } else {
        const err = await resp.json();
        showMessage('error', err.detail || 'Failed to sync vendors');
      }
    } catch (err) {
      showMessage('error', 'Failed to sync vendors');
    }
    setSyncing(false);
  };

  const syncBills = async () => {
    setSyncing(true);
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/sync-bills`, { method: 'POST' });
      if (resp.ok) {
        const data = await resp.json();
        // Show warning if there were push errors
        const msgType = data.push_errors > 0 ? 'warning' : 'success';
        showMessage(msgType, data.message);
        await fetchBills();
      } else {
        const err = await resp.json();
        showMessage('error', err.detail || 'Failed to sync bills');
      }
    } catch (err) {
      showMessage('error', 'Failed to sync bills');
    }
    setSyncing(false);
  };

  const resendAllBillsToMercury = async () => {
    if (!window.confirm('This will reset all previously sent bills and re-email them to Mercury. Continue?')) {
      return;
    }
    setSyncing(true);
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/bills/resend-all`, { method: 'POST' });
      if (resp.ok) {
        const data = await resp.json();
        const msgType = data.failed > 0 ? 'warning' : 'success';
        showMessage(msgType, data.message);
        await fetchBills();
      } else {
        const err = await resp.json();
        showMessage('error', err.detail || 'Failed to resend bills');
      }
    } catch (err) {
      showMessage('error', 'Failed to resend bills');
    }
    setSyncing(false);
  };

  const sendAnyway = async (bill) => {
    if (!window.confirm(`Send bill from "${bill.vendor_name}" to Mercury even though it matches an existing payment?`)) return;
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/bills/${bill.qbo_bill_id}/send-to-mercury?force=true`, { method: 'POST' });
      const data = await resp.json();
      showMessage(resp.ok ? 'success' : 'error', resp.ok ? 'Bill sent to Mercury' : (data.detail || 'Failed to send bill'));
    } catch (err) {
      showMessage('error', 'Failed to send bill');
    }
    await Promise.all([fetchBills(), fetchPaymentStats()]);
  };

  const voidBill = async (billId, vendorName) => {
    if (!window.confirm(`Mark bill from "${vendorName}" as voided? This will hide it from the active bills list.`)) {
      return;
    }
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/bills/${billId}/void`, { method: 'POST' });
      if (resp.ok) {
        showMessage('success', 'Bill marked as voided');
        await Promise.all([fetchBills(), fetchPaymentStats()]);
      } else {
        const err = await resp.json();
        showMessage('error', err.detail || 'Failed to void bill');
      }
    } catch (err) {
      showMessage('error', 'Failed to void bill');
    }
  };

  const unvoidBill = async (billId) => {
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/bills/${billId}/unvoid`, { method: 'POST' });
      if (resp.ok) {
        showMessage('success', 'Bill restored');
        await Promise.all([fetchBills(), fetchPaymentStats()]);
      } else {
        const err = await resp.json();
        showMessage('error', err.detail || 'Failed to restore bill');
      }
    } catch (err) {
      showMessage('error', 'Failed to restore bill');
    }
  };

  const syncPaymentStatus = async () => {
    setSyncing(true);
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/sync-payment-status`, { method: 'POST' });
      if (resp.ok) {
        const data = await resp.json();
        if (data.updated_to_paid > 0) {
          showMessage('success', `${data.updated_to_paid} bills marked as paid from QBO`);
        } else {
          showMessage('info', 'No bills needed status update');
        }
        await fetchBills();
      } else {
        const err = await resp.json();
        showMessage('error', err.detail || 'Failed to sync payment status');
      }
    } catch (err) {
      showMessage('error', 'Failed to sync payment status');
    }
    setSyncing(false);
  };

  // Mercury Payment Functions
  const syncMercuryPayments = async () => {
    setSyncingPayments(true);
    try {
      const resp = await apiFetch(`${API_URL}/api/billpay/payments/sync?days=21`, { method: 'POST' });
      if (resp.ok) {
        const data = await resp.json();
        showMessage('success', `Synced ${data.total_fetched} payments, ${data.matched} matched, ${data.auto_posted} auto-posted`);
        await fetchPaymentStats();
        await fetchBills();
      } else {
        const err = await resp.json();
        showMessage('error', err.detail || 'Failed to sync Mercury payments');
      }
    } catch (err) {
      showMessage('error', 'Failed to sync Mercury payments');
    }
    setSyncingPayments(false);
  };


  const linkVendorToRecipient = async (vendorId, recipientId) => {
    try {
      const resp = await apiFetch(
        `${API_URL}/api/billpay/vendors/${vendorId}/link-recipient?recipient_id=${recipientId}`,
        { method: 'POST' }
      );
      if (resp.ok) {
        showMessage('success', 'Vendor linked to Mercury recipient');
        await fetchVendors();
        // Re-sync bills to auto-push any that can now be pushed
        await syncBills();
      } else {
        const err = await resp.json();
        showMessage('error', err.detail || 'Failed to link vendor');
      }
    } catch (err) {
      showMessage('error', 'Failed to link vendor');
    }
  };

  const getStatusBadge = (billStatus) => {
    const badges = {
      pending_recipient: { color: 'bg-amber-100 text-amber-800', icon: AlertCircle, text: 'Needs Recipient' },
      possible_duplicate: { color: 'bg-red-100 text-red-800', icon: AlertCircle, text: 'Possible Duplicate' },
      queued: { color: 'bg-blue-100 text-blue-800', icon: Clock, text: 'Ready to Push' },
      sent_to_mercury: { color: 'bg-purple-100 text-purple-800', icon: Banknote, text: 'In Mercury Queue' },
      paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, text: 'Paid' },
      voided: { color: 'bg-slate-100 text-slate-600', icon: XCircle, text: 'Voided' },
      deleted_in_qbo: { color: 'bg-slate-100 text-slate-600', icon: XCircle, text: 'Deleted in QBO' },
    };
    const badge = badges[billStatus] || badges.pending_recipient;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Stats
  const vendorsLinked = vendors.filter(v => v.mercury_recipient_id).length;
  const billsPending = bills.filter(b => b.status === 'pending_recipient').length;
  const billsQueued = bills.filter(b => b.status === 'queued').length;
  // Total amount of unpaid bills (not yet paid or voided)
  const totalUnpaid = bills
    .filter(b => b.status !== 'paid' && b.status !== 'voided' && b.status !== 'deleted_in_qbo')
    .reduce((sum, b) => sum + (b.amount || 0), 0);

  if (loading) {
    return (
      <AdminSidebar pageTitle="Bill Pay" pageIcon={CreditCard}>
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar pageTitle="Bill Pay" pageIcon={CreditCard}>
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Banknote className="w-8 h-8 text-emerald-600" />
            Bill Pay Integration
          </h1>
          <p className="text-slate-600 mt-2">
            Sync bills from QuickBooks Online to Mercury for payment
          </p>
          {paymentCounts && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="billpay-header-counts">
              {[
                { key: 'proposed', label: 'Proposed', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
                { key: 'already_paid', label: 'Already paid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                { key: 'unmatched', label: 'Unmatched', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
                { key: 'possible_duplicate', label: 'Possible duplicate bills', cls: 'bg-red-50 text-red-700 border-red-200' },
              ].map(c => (
                <span key={c.key} data-testid={`billpay-count-${c.key}`} className={`px-3 py-1 rounded-full border text-sm font-medium ${c.cls}`}>
                  {c.label}: {paymentCounts[c.key] ?? 0}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
            message.type === 'warning' ? 'bg-amber-50 text-amber-800 border border-amber-200' :
            'bg-blue-50 text-blue-800 border border-blue-200'
          }`}>
            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : 
             message.type === 'error' ? <XCircle className="w-5 h-5" /> : 
             <AlertCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {/* Connection Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* QBO Status */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">QuickBooks Online</h3>
                  <p className="text-sm text-slate-500">Accounting & Bills</p>
                </div>
              </div>
              {status?.qbo?.connected ? (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-sm font-medium">
                  <XCircle className="w-4 h-4" />
                  Not Connected
                </span>
              )}
            </div>
            {status?.qbo?.connected ? (
              <div className="text-sm text-slate-600">
                <p>Realm ID: <code className="bg-slate-100 px-2 py-0.5 rounded">{status.qbo.realm_id}</code></p>
              </div>
            ) : (
              <a
                href={`${API_URL}/api/qbo/auth`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Link2 className="w-4 h-4" />
                Connect QuickBooks
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>

          {/* Mercury Status */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Mercury Bank</h3>
                  <p className="text-sm text-slate-500">Payments & Banking</p>
                </div>
              </div>
              {status?.mercury?.connected ? (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-sm font-medium">
                  <XCircle className="w-4 h-4" />
                  Not Connected
                </span>
              )}
            </div>
            {status?.mercury?.connected && status.mercury.accounts?.length > 0 ? (
              <div className="text-sm text-slate-600">
                <p className="mb-2">{status.mercury.accounts.length} account(s) available:</p>
                <div className="space-y-1">
                  {status.mercury.accounts.slice(0, 3).map((acc, i) => (
                    <div key={i} className="bg-slate-50 px-3 py-1.5 rounded text-xs">
                      {acc.name}
                    </div>
                  ))}
                  {status.mercury.accounts.length > 3 && (
                    <p className="text-xs text-slate-400">+{status.mercury.accounts.length - 3} more</p>
                  )}
                </div>
              </div>
            ) : status?.mercury?.error ? (
              <div className="mt-2">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="text-amber-800 font-medium">Configuration Required</p>
                      <p className="text-amber-700 mt-1">{status.mercury.error}</p>
                      <a 
                        href="https://app.mercury.com/settings/api-tokens" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-amber-800 hover:text-amber-900 font-medium"
                      >
                        Open Mercury Dashboard
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                Mercury API token is configured via environment variables. Contact your administrator if connection issues persist.
              </p>
            )}
          </div>
        </div>

        {/* Stats Row */}
        {status?.qbo?.connected && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{vendors.length}</p>
                  <p className="text-sm text-slate-500">Vendors</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{vendorsLinked}</p>
                  <p className="text-sm text-slate-500">Linked to Mercury</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{bills.length}</p>
                  <p className="text-sm text-slate-500">Bills Synced</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalUnpaid)}</p>
                  <p className="text-sm text-slate-500">Unpaid Total</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        {status?.qbo?.connected && (
          <>
            <div className="flex gap-2 mb-6 border-b overflow-x-auto">
              {['overview', 'vendors', 'bills', 'payments'].map((tab) => (
                <button
                  key={tab}
                  data-testid={`billpay-tab-${tab}`}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 font-medium capitalize transition-colors whitespace-nowrap shrink-0 ${
                    activeTab === tab
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab === 'payments' ? 'Mercury Payments' : tab}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Quick Actions */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={syncVendors}
                      disabled={syncing}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                    >
                      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Sync Vendors from QBO
                    </button>
                    <button
                      onClick={syncBills}
                      disabled={syncing}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                    >
                      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Sync Bills from QBO
                    </button>
                    <button
                      onClick={resendAllBillsToMercury}
                      disabled={syncing}
                      data-testid="resend-all-to-mercury-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                      title="Reset and re-email all bills to Mercury Bill Pay"
                    >
                      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Resend All to Mercury
                    </button>
                    <button
                      onClick={loadAllData}
                      disabled={syncing}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Refresh All
                    </button>
                  </div>
                </div>

                {/* Pending Actions */}
                {(billsPending > 0 || vendors.filter(v => !v.mercury_recipient_id).length > 0) && (
                  <div className="bg-amber-50 rounded-xl border border-amber-200 p-6">
                    <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Action Required
                    </h3>
                    <ul className="space-y-2 text-sm text-amber-700">
                      {vendors.filter(v => !v.mercury_recipient_id).length > 0 && (
                        <li className="flex items-center gap-2">
                          <ArrowRight className="w-4 h-4" />
                          {vendors.filter(v => !v.mercury_recipient_id).length} vendor(s) need to be linked to Mercury recipients
                        </li>
                      )}
                      {billsPending > 0 && (
                        <li className="flex items-center gap-2">
                          <ArrowRight className="w-4 h-4" />
                          {billsPending} bill(s) waiting for vendor recipient setup
                        </li>
                      )}
                    </ul>
                    <button
                      onClick={() => setActiveTab('vendors')}
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                    >
                      <Users className="w-4 h-4" />
                      Manage Vendors
                    </button>
                  </div>
                )}

                {/* Recent Bills */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Recent Bills</h3>
                  {bills.length === 0 ? (
                    <p className="text-slate-500 text-sm">No bills synced yet. Click &quot;Sync Bills from QBO&quot; to get started.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            <th className="pb-3">Vendor</th>
                            <th className="pb-3">Amount</th>
                            <th className="pb-3">Due Date</th>
                            <th className="pb-3">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {bills.slice(0, 5).map((bill) => (
                            <tr key={bill.qbo_bill_id} className="text-sm">
                              <td className="py-3 font-medium text-slate-900">{bill.vendor_name}</td>
                              <td className="py-3 text-slate-600">{formatCurrency(bill.amount)}</td>
                              <td className="py-3 text-slate-600">{formatDate(bill.due_date)}</td>
                              <td className="py-3">{getStatusBadge(bill.status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {bills.length > 5 && (
                        <button
                          onClick={() => setActiveTab('bills')}
                          className="mt-4 text-sm text-blue-600 hover:text-blue-700"
                        >
                          View all {bills.length} bills →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Vendors Tab */}
            {activeTab === 'vendors' && (
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Vendors</h3>
                  <button
                    onClick={syncVendors}
                    disabled={syncing}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                  >
                    {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Sync from QBO
                  </button>
                </div>
                
                {/* Vendor Search Bar */}
                {vendors.length > 0 && (
                  <div className="p-4 border-b bg-slate-50">
                    <div className="relative max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search vendors by name..."
                        value={vendorSearch}
                        onChange={(e) => setVendorSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        data-testid="vendor-search-input"
                      />
                      {vendorSearch && (
                        <button
                          onClick={() => setVendorSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Showing {getFilteredVendors().length} of {vendors.length} vendors
                      {vendorSearch && ` matching "${vendorSearch}"`}
                    </p>
                  </div>
                )}
                
                {vendors.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No vendors synced yet.</p>
                    <button
                      onClick={syncVendors}
                      className="mt-4 text-blue-600 hover:text-blue-700"
                    >
                      Sync vendors from QuickBooks
                    </button>
                  </div>
                ) : getFilteredVendors().length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No vendors match &quot;{vendorSearch}&quot;</p>
                    <button
                      onClick={() => setVendorSearch('')}
                      className="mt-4 text-blue-600 hover:text-blue-700"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {getFilteredVendors().map((vendor) => (
                      <div key={vendor.qbo_vendor_id} className="p-4">
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => setExpandedVendor(expandedVendor === vendor.qbo_vendor_id ? null : vendor.qbo_vendor_id)}
                        >
                          <div className="flex items-center gap-3">
                            {expandedVendor === vendor.qbo_vendor_id ? 
                              <ChevronDown className="w-4 h-4 text-slate-400" /> : 
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            }
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-slate-500" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{vendor.qbo_vendor_name}</p>
                              <p className="text-xs text-slate-500">QBO ID: {vendor.qbo_vendor_id}</p>
                            </div>
                          </div>
                          {vendor.mercury_recipient_id ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                              <CheckCircle2 className="w-3 h-3" />
                              Linked
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                              <AlertCircle className="w-3 h-3" />
                              Not Linked
                            </span>
                          )}
                        </div>
                        
                        {/* Expanded View */}
                        {expandedVendor === vendor.qbo_vendor_id && (
                          <div className="mt-4 ml-7 p-4 bg-slate-50 rounded-lg">
                            {vendor.mercury_recipient_id ? (
                              <div className="text-sm">
                                <p className="text-slate-600">
                                  <strong>Mercury Recipient ID:</strong>{' '}
                                  <code className="bg-white px-2 py-0.5 rounded border">{vendor.mercury_recipient_id}</code>
                                </p>
                              </div>
                            ) : (
                              <div>
                                <p className="text-sm text-slate-600 mb-3">
                                  Link this vendor to a Mercury recipient to enable bill payments:
                                </p>
                                {mercuryRecipients.length > 0 ? (
                                  <div className="relative" ref={recipientDropdownOpen === vendor.qbo_vendor_id ? dropdownRef : null}>
                                    {/* Dropdown Trigger */}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setRecipientDropdownOpen(recipientDropdownOpen === vendor.qbo_vendor_id ? null : vendor.qbo_vendor_id);
                                        setRecipientSearch('');
                                      }}
                                      className="w-full flex items-center justify-between px-4 py-2.5 bg-white border rounded-lg hover:border-blue-400 transition-colors text-left"
                                      data-testid={`recipient-dropdown-${vendor.qbo_vendor_id}`}
                                    >
                                      <span className="text-slate-500">Select a Mercury recipient...</span>
                                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${recipientDropdownOpen === vendor.qbo_vendor_id ? 'rotate-180' : ''}`} />
                                    </button>
                                    
                                    {/* Dropdown Menu */}
                                    {recipientDropdownOpen === vendor.qbo_vendor_id && (
                                      <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-hidden">
                                        {/* Search Input */}
                                        <div className="p-2 border-b sticky top-0 bg-white">
                                          <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <input
                                              type="text"
                                              placeholder="Search recipients..."
                                              value={recipientSearch}
                                              onChange={(e) => setRecipientSearch(e.target.value)}
                                              className="w-full pl-9 pr-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                              autoFocus
                                              data-testid={`recipient-search-${vendor.qbo_vendor_id}`}
                                            />
                                          </div>
                                        </div>
                                        
                                        {/* Recipients List */}
                                        <div className="overflow-y-auto max-h-48">
                                          {getFilteredRecipients().length > 0 ? (
                                            getFilteredRecipients().map((recipient) => (
                                              <button
                                                key={recipient.id}
                                                onClick={() => {
                                                  linkVendorToRecipient(vendor.qbo_vendor_id, recipient.id);
                                                  setRecipientDropdownOpen(null);
                                                  setRecipientSearch('');
                                                }}
                                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-b-0"
                                                data-testid={`recipient-option-${recipient.id}`}
                                              >
                                                <Link2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                <span className="truncate">{recipient.name || recipient.id}</span>
                                              </button>
                                            ))
                                          ) : (
                                            <div className="px-4 py-3 text-sm text-slate-500 text-center">
                                              No recipients match &quot;{recipientSearch}&quot;
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-sm text-amber-600">
                                    No Mercury recipients found. Add recipients in Mercury first, then refresh.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bills Tab */}
            {activeTab === 'bills' && (
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="p-4 border-b flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h3 className="font-semibold text-slate-900">Bills from QBO</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Bills are synced from QuickBooks and emailed to Mercury Bill Pay for approval.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={syncPaymentStatus}
                      disabled={syncing}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                      data-testid="sync-payment-status-btn"
                      title="Check QBO for paid bills and update status"
                    >
                      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      Sync Paid Status
                    </button>
                    <button
                      onClick={syncBills}
                      disabled={syncing}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                      data-testid="sync-bills-btn"
                    >
                      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Sync Bills from QBO
                    </button>
                  </div>
                </div>
                
                <div className="p-3 bg-green-50 border-b border-green-100 flex items-start gap-2 text-sm text-green-800">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Email-to-Bill Pay:</strong> When you sync bills, linked vendors&apos; bills are automatically 
                    emailed as PDFs to Mercury Bill Pay for your approval.
                  </div>
                </div>
                
                {/* Filter Bar */}
                <div className="p-4 border-b bg-slate-50">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: 'needs_action', label: 'Needs Action', color: 'bg-amber-100 text-amber-800 border-amber-200' },
                        { key: 'unpaid', label: 'Unpaid', color: 'bg-blue-100 text-blue-800 border-blue-200' },
                        { key: 'pending_recipient', label: 'Unlinked Vendor', color: 'bg-red-100 text-red-800 border-red-200' },
                        { key: 'sent_to_mercury', label: 'Sent to Mercury', color: 'bg-green-100 text-green-800 border-green-200' },
                        { key: 'possible_duplicate', label: 'Possible Duplicate', color: 'bg-red-50 text-red-700 border-red-200' },
                        { key: 'paid', label: 'Paid', color: 'bg-slate-100 text-slate-600 border-slate-200' },
                        { key: 'voided', label: 'Voided', color: 'bg-slate-100 text-slate-500 border-slate-200' },
                        { key: 'all', label: 'All', color: 'bg-slate-100 text-slate-600 border-slate-200' },
                      ].map(filter => {
                        const counts = getBillCounts();
                        const count = counts[filter.key] || 0;
                        const isActive = billFilter === filter.key;
                        return (
                          <button
                            key={filter.key}
                            data-testid={`billpay-filter-${filter.key}`}
                            onClick={() => setBillFilter(filter.key)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                              isActive 
                                ? `${filter.color} ring-2 ring-offset-1 ring-blue-500` 
                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            {filter.label} ({count})
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <input
                        type="text"
                        placeholder="Search by vendor, memo, or invoice #..."
                        value={billSearch}
                        onChange={(e) => setBillSearch(e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
                
                {bills.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No bills synced yet.</p>
                    <p className="text-sm mt-2">Click &quot;Sync Bills from QBO&quot; to fetch unpaid bills.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    {getFilteredBills().length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No bills match the current filter.</p>
                        <button 
                          onClick={() => { setBillFilter('all'); setBillSearch(''); }}
                          className="text-sm text-blue-600 hover:underline mt-2"
                        >
                          Clear filters
                        </button>
                      </div>
                    ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider bg-slate-50">
                          <th className="px-4 py-3">Vendor</th>
                          <th className="px-4 py-3">Amount</th>
                          <th className="px-4 py-3">Due Date</th>
                          <th className="px-4 py-3">Invoice #</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {getFilteredBills().map((bill) => (
                          <tr key={bill.qbo_bill_id} className={`text-sm hover:bg-slate-50 ${bill.status === 'voided' || bill.status === 'deleted_in_qbo' ? 'opacity-50' : ''}`}>
                            <td className="px-4 py-3 font-medium text-slate-900">{bill.vendor_name}</td>
                            <td className="px-4 py-3 text-slate-900 font-medium">{formatCurrency(bill.amount)}</td>
                            <td className="px-4 py-3 text-slate-600">{formatDate(bill.due_date)}</td>
                            <td className="px-4 py-3 text-slate-500 max-w-[150px] truncate" title={bill.invoice_number || bill.memo || '—'}>
                              {bill.invoice_number || bill.memo || '—'}
                            </td>
                            <td className="px-4 py-3">
                              {getStatusBadge(bill.status)}
                              {bill.status === 'pending_recipient' && (
                                <p className="text-xs text-amber-600 mt-1">Link vendor in Vendors tab</p>
                              )}
                              {bill.status === 'possible_duplicate' && (
                                <p className="text-xs text-red-600 mt-1" data-testid={`bill-duplicate-info-${bill.qbo_bill_id}`}>
                                  Mercury already paid
                                  {bill.mercury_payment_amount != null ? ` $${Number(bill.mercury_payment_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ''}
                                  {bill.mercury_payment_date ? ` on ${new Date(bill.mercury_payment_date).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}` : ''}
                                </p>
                              )}
                              {bill.sent_to_mercury_at && (
                                <p className="text-xs text-green-600 mt-1">
                                  Emailed to Mercury
                                </p>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {bill.status === 'deleted_in_qbo' ? (
                                <span className="text-xs text-slate-400" data-testid={`bill-deleted-note-${bill.qbo_bill_id}`}>Deleted in QBO</span>
                              ) : bill.status === 'voided' ? (
                                <button
                                  onClick={() => unvoidBill(bill.qbo_bill_id)}
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                  title="Restore this bill"
                                >
                                  Restore
                                </button>
                              ) : (
                                <div className="flex gap-3">
                                {bill.status === 'possible_duplicate' && (
                                  <>
                                  <button
                                    data-testid={`bill-send-anyway-${bill.qbo_bill_id}`}
                                    onClick={() => sendAnyway(bill)}
                                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                    title="Not a duplicate - send this bill to Mercury"
                                  >
                                    Send anyway
                                  </button>
                                  <button
                                    data-testid={`bill-not-needed-${bill.qbo_bill_id}`}
                                    onClick={() => voidBill(bill.qbo_bill_id, bill.vendor_name)}
                                    className="text-xs text-slate-600 hover:text-slate-800 hover:underline"
                                    title="Already paid - clear the hold (voids locally only, not in QuickBooks)"
                                  >
                                    Mark not needed
                                  </button>
                                  </>
                                )}
                                {bill.status !== 'possible_duplicate' && (
                                <button
                                  data-testid={`bill-void-${bill.qbo_bill_id}`}
                                  onClick={() => voidBill(bill.qbo_bill_id, bill.vendor_name)}
                                  className="text-xs text-red-600 hover:text-red-800 hover:underline"
                                  title="Mark as voided (e.g., if voided in QBO)"
                                >
                                  Void
                                </button>
                                )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Mercury Payments Tab */}
            {activeTab === 'payments' && (
              <MercuryPaymentsTab
                apiFetch={apiFetch}
                apiUrl={API_URL}
                showMessage={showMessage}
                onSyncPayments={syncMercuryPayments}
                syncing={syncingPayments}
                onChanged={() => Promise.all([fetchPaymentStats(), fetchBills()])}
              />
            )}
          </>
        )}

        {/* Not Connected State */}
        {!status?.qbo?.connected && (
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Link2 className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Connect QuickBooks Online</h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Connect your QuickBooks Online account to start syncing vendors and bills to Mercury for payment.
            </p>
            <a
              href={`${API_URL}/api/qbo/auth`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              <Link2 className="w-5 h-5" />
              Connect QuickBooks Online
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </div>
    </AdminSidebar>
  );
}
